// Récupération de la clé maîtresse — Shamir 2-de-3.
//
// Sans elle, un mot de passe oublié détruit un cabinet : la clé maîtresse n'est
// déballable que par une clé dérivée de ce mot de passe, et le serveur ne peut
// RIEN — c'est la promesse du produit, et c'est aussi son piège.
//
// Le filet : la clé est découpée en 3 parts dont 2 suffisent. Une pour l'avocat
// (qu'il exporte), deux pour des dépositaires. Le serveur n'en détient jamais
// assez pour reconstituer quoi que ce soit.

import { useCallback, useEffect, useState } from 'react';
import {
  acceptShare,
  getConfiguration,
  listDepositaries,
  listHeldShares,
  setupRecovery,
  type Configuration,
  type Depositary,
  type HeldShare,
} from '../crypto/recovery';
import { useAuth } from './store';

const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

const toBase64 = (b: Uint8Array) => btoa(String.fromCharCode(...b));

/** Le fichier que l'avocat doit mettre à l'abri. */
function telechargerPart(email: string, share: Uint8Array): void {
  const contenu = `DELO — PART DE RÉCUPÉRATION
Compte : ${email}
Émise le : ${new Date().toLocaleString('fr-FR')}

⚠️ CONSERVE CE FICHIER HORS LIGNE (impression, coffre, gestionnaire de secrets).
Il ne sera JAMAIS réaffiché : le serveur ne le détient pas.

Avec ce document ET la part d'UN de tes deux dépositaires, tu peux retrouver
l'accès à ton compte même si tu as oublié ton mot de passe.

Seul, ce document ne révèle rien.

--- PART (à recopier telle quelle) ---
${toBase64(share)}
--- FIN ---
`;
  const blob = new Blob([contenu], { type: 'text/plain;charset=utf-8' });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = 'delo-part-de-recuperation.txt';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(href);
  }, 60_000);
}

// ---------------------------------------------------------------------------

export function Recovery() {
  const session = useAuth((s) => s.session);
  const masterKey = useAuth((s) => s.masterKey);
  const email = useAuth((s) => s.email);

  const [config, setConfig] = useState<Configuration | null>(null);
  const [depositaries, setDepositaries] = useState<Depositary[]>([]);
  const [chosen, setChosen] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const refresh = useCallback(async () => {
    if (!session) return;
    try {
      const [c, d] = await Promise.all([
        getConfiguration(session.sessionToken),
        listDepositaries(session.sessionToken),
      ]);
      setConfig(c);
      setDepositaries(d);
    } catch (e) {
      setError(message(e));
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (!session || !masterKey || loading) return null;

  async function mettreEnPlace() {
    setBusy(true);
    setError(null);
    try {
      const pair = chosen
        .map((id) => depositaries.find((d) => d.id === id))
        .filter(Boolean) as [Depositary, Depositary];

      const { configuration, selfShare } = await setupRecovery(
        session!.sessionToken,
        masterKey!.masterKey,
        pair,
      );

      // La part de l'avocat n'existe QUE ici : le serveur ne la détient pas.
      telechargerPart(email, selfShare);
      setConfig(configuration);
      setDone(true);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }

  // --- Déjà en place : on montre l'état ---
  if (config) {
    return (
      <section
        className="mt-6 rounded border border-slate-200 p-4 text-left"
        data-testid="recovery-status"
      >
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Récupération du compte
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          Ta clé est répartie en {config.totalShares} parts, dont{' '}
          <strong>{config.threshold} suffisent</strong>. Le serveur n&apos;en
          détient jamais assez pour la reconstituer.
        </p>

        <ul className="mt-3 space-y-1 text-sm">
          {config.shares.map((s) => (
            <li key={s.id} className="flex gap-3">
              <span className="text-slate-600">
                {s.depositaryType === 'self'
                  ? 'Ta part (exportée)'
                  : (s.depositaryEmail ?? 'dépositaire')}
              </span>
              <span
                className={`ml-auto text-xs ${
                  s.status === 'active' ? 'text-emerald-700' : 'text-amber-700'
                }`}
              >
                {s.status === 'active' ? 'garde acceptée' : 'en attente'}
              </span>
            </li>
          ))}
        </ul>

        <p
          data-testid="recovery-verified"
          className={`mt-3 text-xs ${config.lastVerifiedAt ? 'text-emerald-700' : 'text-amber-700'}`}
        >
          {config.lastVerifiedAt
            ? 'Reconstitution prouvée : les parts redonnent bien ta clé — le serveur s’en est assuré sans jamais la voir.'
            : 'Reconstitution non vérifiée.'}
        </p>

        {done && (
          <p className="mt-3 rounded bg-amber-50 p-2 text-xs text-amber-800">
            Ta part vient d&apos;être téléchargée. <strong>Mets-la à l&apos;abri</strong> —
            elle ne sera jamais réaffichée.
          </p>
        )}
      </section>
    );
  }

  // --- Pas encore en place ---
  return (
    <section
      className="mt-6 rounded border border-amber-200 bg-amber-50 p-4 text-left"
      data-testid="recovery-setup"
    >
      <h3 className="text-sm font-semibold text-amber-900">
        Ton compte n&apos;a pas de filet
      </h3>
      <p className="mt-1 text-xs text-amber-800">
        Ta clé maîtresse ne se déballe qu&apos;avec ton mot de passe.{' '}
        <strong>Si tu l&apos;oublies, personne — pas même nous — ne pourra te
        rendre tes dossiers.</strong>
      </p>
      <p className="mt-2 text-xs text-amber-800">
        Découpe-la en 3 parts dont 2 suffisent : une pour toi, deux pour des
        confrères. Aucun d&apos;eux ne peut rien seul.
      </p>

      {depositaries.length < 2 ? (
        <p className="mt-3 text-xs text-amber-700" data-testid="not-enough-depositaries">
          Il faut au moins deux autres membres du cabinet connectés au moins une
          fois (ils publient alors leur clé de récupération). Invite-les, puis
          reviens ici.
        </p>
      ) : (
        <>
          <p className="mt-3 text-xs font-medium text-amber-900">
            Choisis exactement deux dépositaires :
          </p>
          <ul className="mt-2 space-y-1" data-testid="depositary-list">
            {depositaries.map((d) => (
              <li key={d.id}>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    data-testid="depositary-choice"
                    checked={chosen.includes(d.id)}
                    onChange={(e) =>
                      setChosen((c) =>
                        e.target.checked
                          ? [...c, d.id]
                          : c.filter((x) => x !== d.id),
                      )
                    }
                  />
                  {d.email}
                </label>
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={mettreEnPlace}
            disabled={busy || chosen.length !== 2}
            data-testid="setup-recovery"
            className="mt-3 w-full rounded bg-blue-900 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50"
          >
            {busy ? 'Découpage et scellement…' : 'Mettre en place la récupération'}
          </button>
          <p className="mt-2 text-[10px] text-amber-700">
            Ta part sera téléchargée immédiatement, et une seule fois.
          </p>
        </>
      )}

      {error && (
        <p data-testid="recovery-error" className="mt-3 text-sm text-red-600">
          {error}
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

/** Les parts qu'on m'a confiées, à accepter en garde. */
export function HeldShares() {
  const session = useAuth((s) => s.session);
  const masterKey = useAuth((s) => s.masterKey);

  const [shares, setShares] = useState<HeldShare[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!session) return;
    try {
      setShares(await listHeldShares(session.sessionToken));
    } catch (e) {
      setError(message(e));
    }
  }, [session]);

  useEffect(() => {
    void refresh();
    const id = setInterval(() => void refresh(), 8000);
    return () => clearInterval(id);
  }, [refresh]);

  if (!session || !masterKey) return null;

  const enAttente = shares.filter((s) => s.status === 'pending');
  if (enAttente.length === 0) return null;

  async function accepter(share: HeldShare) {
    setBusy(share.id);
    setError(null);
    try {
      // On descelle RÉELLEMENT la part et on vérifie son empreinte avant
      // d'accepter : une garde qu'on ne peut pas ouvrir est une garde fictive,
      // et on ne s'en apercevrait que le jour d'une récupération.
      await acceptShare(session!.sessionToken, masterKey!.masterKey, share);
      await refresh();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section
      className="mt-6 rounded border border-blue-200 bg-blue-50 p-4 text-left"
      data-testid="held-shares"
    >
      <h3 className="text-sm font-semibold text-blue-900">
        On te confie une part de récupération
      </h3>
      <p className="mt-1 text-xs text-blue-800">
        Elle ne te révèle rien : seule, elle est inutile. Mais réunie à une autre,
        elle permettra de rendre son compte à un confrère qui aurait oublié son
        mot de passe.
      </p>

      <ul className="mt-3 space-y-2">
        {enAttente.map((s) => (
          <li key={s.id} className="flex items-center gap-3 text-sm">
            <span className="text-slate-700">{s.ownerEmail}</span>
            <button
              type="button"
              onClick={() => accepter(s)}
              disabled={busy === s.id}
              data-testid="accept-share"
              className="ml-auto rounded bg-blue-900 px-3 py-2 text-xs font-medium text-white hover:bg-blue-800 disabled:opacity-50"
            >
              {busy === s.id ? 'Vérification…' : 'Accepter la garde'}
            </button>
          </li>
        ))}
      </ul>

      {error && (
        <p data-testid="held-shares-error" className="mt-2 text-sm text-red-600">
          {error}
        </p>
      )}
    </section>
  );
}
